import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from './ThemeContext';
import { useConfirm } from './ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import logoLight from './../Images/tsqs-hm-ft-logo1.gif';
import logoDark from './../Images/tsqs-hm-ft-logo.gif';
import picon from './../Images/profile_icon.png';
import './AppHeader.css';

function SunIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

/**
 * Shared AppHeader used across Employee, Admin, MainManager, LeadDashboard.
 * Props:
 *   title          — page title string (center)
 *   employeeInfo   — { full_name, job_title }
 *   onProfileClick — callback when avatar/name is clicked
 *   rightContent   — optional extra JSX rendered right of user info
 */
export default function AppHeader({ title, employeeInfo = {}, onProfileClick, rightContent, hideLogo = false }) {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isProfileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const confirm = useConfirm();

  const handleLogout = async () => {
    setProfileMenuOpen(false);
    const ok = await confirm({
      title: 'Confirm Logout',
      message: 'Are you sure you want to log out?',
      confirmLabel: 'Log Out',
      cancelLabel: 'Stay',
      //variant: 'danger'
    });
    if (!ok) return;
    localStorage.clear();
    navigate('/');
  };

  const handleProfileToggle = () => {
    setProfileMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="app-header">
      {/* Left: Logo (conditionally rendered) */}
      <div className="app-header-left">
        {!hideLogo && (
          <>
            <img src={logoLight} alt="TSQS Logo" className="app-header-logo logo-light" />
            <img src={logoDark} alt="TSQS Logo" className="app-header-logo logo-dark" />
          </>
        )}
      </div>

      {/* Center: Title */}
      <div className="app-header-center">
        <h1 className="app-header-title">{title || 'PERFORMANCE MANAGEMENT'}</h1>
      </div>

      {/* Right: Actions */}
      <div className="app-header-right">
        {rightContent}

        {/* Theme toggle */}
        {/* THEME TOGGLE - Uncomment to re-enable dark mode toggle
        <button
          className="app-header-theme-toggle"
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
        */}

        {/* User info */}
        {employeeInfo.full_name && (
          <div className="app-header-user-wrapper" ref={profileMenuRef}>
            <button className="app-header-user" onClick={handleProfileToggle} title="Open profile menu">
              <img src={picon} alt="Profile" className="app-header-avatar" />
              <div className="app-header-user-info">
                <span className="app-header-user-name">{employeeInfo.full_name}</span>
                {employeeInfo.job_title && (
                  <span className="app-header-user-role">{employeeInfo.job_title}</span>
                )}
              </div>
              <span className={`app-header-dropdown-icon ${isProfileMenuOpen ? 'open' : ''}`}>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
            </button>
            {isProfileMenuOpen && (
              <div className="app-header-menu">
                <button className="app-header-menu-item" type="button" onClick={() => { setProfileMenuOpen(false); onProfileClick?.(); }}>
                  Profile Settings
                </button>
                <button className="app-header-menu-item" type="button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
